import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ApplicantContractFeeSettingsApi from "../../../../../infrastructure/api/applicantContractFeeSettings";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const formatRial = (value) =>
  Number(value || 0).toLocaleString("fa-IR");

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fa-IR");
  } catch {
    return String(value);
  }
};

const ApplicantContractFeeSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    meterDayPriceRial: "",
    installationWageRial: "",
    platformFeeRial: "",
    notes: "",
    changeReason: "",
  });
  const [meta, setMeta] = useState({ updateOn: null, updateBy: "" });

  const packageTotal = useMemo(() => {
    const a = Number(form.meterDayPriceRial) || 0;
    const b = Number(form.installationWageRial) || 0;
    const c = Number(form.platformFeeRial) || 0;
    return Math.max(0, Math.round(a) + Math.round(b) + Math.round(c));
  }, [form]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [currentRes, historyRes] = await Promise.all([
        ApplicantContractFeeSettingsApi.getCurrent(),
        ApplicantContractFeeSettingsApi.getHistory(50),
      ]);
      if (!currentRes.ok) {
        toast.error(currentRes.message || "بارگذاری تنظیمات ناموفق بود.");
      } else {
        const d = currentRes.data || {};
        setForm({
          meterDayPriceRial: String(d.meterDayPriceRial ?? d.MeterDayPriceRial ?? 0),
          installationWageRial: String(
            d.installationWageRial ?? d.InstallationWageRial ?? 0
          ),
          platformFeeRial: String(d.platformFeeRial ?? d.PlatformFeeRial ?? 0),
          notes: d.notes ?? d.Notes ?? "",
          changeReason: "",
        });
        setMeta({
          updateOn: d.updateOn ?? d.UpdateOn ?? null,
          updateBy: d.updateBy ?? d.UpdateBy ?? "",
        });
      }
      if (historyRes.ok) {
        const rows = historyRes.data;
        setHistory(Array.isArray(rows) ? rows : []);
      } else {
        setHistory([]);
      }
    } catch (e) {
      toast.error(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await ApplicantContractFeeSettingsApi.update({
        meterDayPriceRial: Number(form.meterDayPriceRial) || 0,
        installationWageRial: Number(form.installationWageRial) || 0,
        platformFeeRial: Number(form.platformFeeRial) || 0,
        notes: form.notes || null,
        changeReason: form.changeReason || null,
      });
      if (!res.ok) {
        toast.error(res.message || "ذخیره ناموفق بود.");
        return;
      }
      toast.success(res.message || "تنظیمات ذخیره شد.");
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={5}>
        <Card>
          <CardHeader
            title="تنظیمات هزینه کنتور و اتصال"
            subheader="مبالغ پیش‌فرض برای تولید قرارداد متقاضی"
          />
          <CardContent>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  size="small"
                  type="number"
                  label="قیمت روز کنتور (ریال)"
                  value={form.meterDayPriceRial}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, meterDayPriceRial: e.target.value }))
                  }
                  inputProps={{ min: 0, step: 1000 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="دستمزد نصب (ریال)"
                  value={form.installationWageRial}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      installationWageRial: e.target.value,
                    }))
                  }
                  inputProps={{ min: 0, step: 1000 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="کارمزد پلتفرم (ریال)"
                  value={form.platformFeeRial}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, platformFeeRial: e.target.value }))
                  }
                  inputProps={{ min: 0, step: 1000 }}
                />
                <Typography variant="body2" fontWeight={700}>
                  جمع: {formatRial(packageTotal)} ریال
                </Typography>
                <TextField
                  size="small"
                  label="یادداشت"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, notes: e.target.value }))
                  }
                  multiline
                  minRows={2}
                />
                <TextField
                  size="small"
                  label="دلیل تغییر (تاریخچه)"
                  value={form.changeReason}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, changeReason: e.target.value }))
                  }
                />
                {(meta.updateOn || meta.updateBy) && (
                  <Typography variant="caption" color="text.secondary">
                    آخرین ویرایش: {formatDateTime(meta.updateOn)}
                    {meta.updateBy ? ` — ${meta.updateBy}` : ""}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={handleSave}
                >
                  ذخیره تنظیمات
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Card>
          <CardHeader
            avatar={<HistoryIcon color="action" />}
            title="تاریخچه تغییرات"
            subheader="نسخه‌های قبلی مبالغ قابل مشاهده است"
          />
          <CardContent sx={{ overflowX: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : history.length === 0 ? (
              <Typography color="text.secondary">تاریخچه‌ای ثبت نشده است.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>از تاریخ</TableCell>
                    <TableCell>تا تاریخ</TableCell>
                    <TableCell align="left">کنتور</TableCell>
                    <TableCell align="left">نصب</TableCell>
                    <TableCell align="left">کارمزد</TableCell>
                    <TableCell align="left">جمع</TableCell>
                    <TableCell>دلیل / کاربر</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row) => {
                    const meter =
                      row.meterDayPriceRial ?? row.MeterDayPriceRial ?? 0;
                    const install =
                      row.installationWageRial ?? row.InstallationWageRial ?? 0;
                    const platform =
                      row.platformFeeRial ?? row.PlatformFeeRial ?? 0;
                    const total =
                      row.packageTotalRial ??
                      row.PackageTotalRial ??
                      Number(meter) + Number(install) + Number(platform);
                    const isCurrent = row.isCurrent ?? row.IsCurrent ?? !row.validUntil;
                    return (
                      <TableRow key={row.id ?? row.Id} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={isCurrent ? "جاری" : "بایگانی"}
                            color={isCurrent ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDateTime(row.validFrom ?? row.ValidFrom)}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(row.validUntil ?? row.ValidUntil)}
                        </TableCell>
                        <TableCell>{formatRial(meter)}</TableCell>
                        <TableCell>{formatRial(install)}</TableCell>
                        <TableCell>{formatRial(platform)}</TableCell>
                        <TableCell>{formatRial(total)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {row.changeReason ?? row.ChangeReason ?? "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.createBy ?? row.CreateBy ?? "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ApplicantContractFeeSettingsPage;
